/**
 * 渲染模型派生：`PlayerView`（协议层真源）→ `PublicHandState`（牌桌组件吃的形状）。
 *
 * 为什么需要这一层（§11.3 / ARCHITECTURE §A4）：
 *   - 服务端下发的唯一视图类型是 `PlayerView`（牌是 2 字符 `Card`，含服务端时钟字段）；
 *   - 而 `TableScene` / `useTableFx` / `tableDerive` 这一整套已完成的 UI 吃的是引擎层的
 *     `PublicHandState`（牌是 `CardId` 数字，无时钟字段），本地传手机模式直接喂引擎输出。
 *   两者**不同构**，所以必须有一个纯函数把前者折成后者。这就是 §A4 里说的
 *   `buildTableVM(view, perspectiveSeat)`：客户端唯一产出牌面的地方，永不跨网络。
 *
 * ★ 红线 #1 的第二道防线：服务端已经裁过一次底牌，这里再裁一次。
 *   `HoleCardsView` 是判别联合，只有 `mine` / `shown` 两个分支带 `cards`；
 *   本文件**只从这两个分支取牌**，其余（none / hidden / mucked）一律产出 `null`。
 *   于是即便将来协议多出一个带牌的分支，不改这里就渲染不出来，而不是默认泄漏。
 *
 * ★ 红线 #18：所有金额原样透传，raise-to 仍是 raise-to，这里不做任何增量换算。
 *
 * 纯函数、零 React、零 DOM —— 能在 node 环境直接单测。
 */
import {
  HAND_STATE_SCHEMA_VERSION,
  deckVariantOf,
  evaluateHand,
  tryToCardId,
  type AppliedAction,
  type CardId,
  type Chips,
  type DeckVariant,
  type HandRank,
  type MadeHandInfo,
  type PlayerView,
  type Pot,
  type PotAward,
  type PublicAction,
  type PublicHandConfig,
  type PublicHandPlayer,
  type PublicHandResult,
  type PublicHandState,
  type PublicSeat,
  type PublicShowdownEntry,
  type SeatIndex,
  type ShowdownEntryView,
  type Street,
} from '@poker/shared'

// ============================================================================
// 牌面转换（带防御性裁剪）
// ============================================================================

/**
 * `Card[]` → `CardId[]`，**丢掉所有解析不出来的值**。
 * 用 `tryToCardId` 而不是 `toCardId`：一张脏牌不该让整张牌桌白屏，
 * 少画一张牌是可恢复的（下一份快照就补回来），抛异常不是。
 */
export function toCardIdsSafe(cards: readonly string[] | null | undefined): CardId[] {
  if (cards == null) return []
  const out: CardId[] = []
  for (const c of cards) {
    const id = tryToCardId(c)
    if (id !== null) out.push(id)
  }
  return out
}

/** 底牌对：只有恰好两张合法牌才算数，否则当作「看不见」。 */
function holePair(cards: readonly string[]): readonly [CardId, CardId] | null {
  const ids = toCardIdsSafe(cards)
  const a = ids[0]
  const b = ids[1]
  if (ids.length !== 2 || a === undefined || b === undefined) return null
  return [a, b]
}

/**
 * `MadeHandInfo` → 引擎的 `HandRank`。
 *
 * 协议只给 `{category, label, bestFive}`，缺 `value` 与 `ranks`；而
 * `tableDerive.handLabelsBySeat` 走的是 `formatHandShort(rank)`，它要读 `ranks`
 * 才能拼出「一对 K」这种文案 —— 直接补零会渲染出「一对 undefined」。
 * 所以这里用 `evaluateHand` 对**这五张已公开的牌**重算一次：
 * 五张牌的最佳五张就是它自己，结果与服务端的 `label` 必然一致，且零信息增量。
 *
 * ★ 必须按房间玩法算：短牌的 A-6-7-8-9 是顺子，按标准规则只是 A 高。
 *   玩法参数刻意不给默认值，漏传就编译不过。
 */
export function rankFromMadeHand(info: MadeHandInfo, variant: DeckVariant): HandRank | null {
  const five = toCardIdsSafe(info.bestFive)
  if (five.length !== 5) return null
  try {
    return evaluateHand(five, variant)
  } catch {
    // 牌不属于这个玩法（服务端数据异常）：当作算不出，调用方会退回服务端给的文案
    return null
  }
}

/** 房间玩法。老服务端下发的配置里没有这个字段，按标准局处理。 */
export function variantOfView(view: PlayerView): DeckVariant {
  return deckVariantOf(view.room.config)
}

// ============================================================================
// 逐段投影
// ============================================================================

function projectConfig(view: PlayerView, handId: string): PublicHandConfig {
  const c = view.room.config
  return {
    handId,
    maxSeats: c.maxSeats,
    smallBlind: c.smallBlind,
    bigBlind: c.bigBlind,
    ante: c.ante,
    anteMode: c.anteMode,
    // 协议不下发烧牌开关（它不影响任何可见状态），UI 也从不读它；给引擎默认值占位
    burnCards: true,
    showdownPolicy: c.showdownPolicy,
    deckVariant: deckVariantOf(c),
  }
}

/** `PublicAction`（协议）→ `AppliedAction`（引擎）。字段一一对应，只有两处改名。 */
function projectAction(a: PublicAction): AppliedAction {
  return {
    index: a.index,
    street: a.street,
    seat: a.seat,
    type: a.type,
    to: a.to,
    delta: a.delta,
    allIn: a.allIn,
    reopenedAction: a.reopened,
    // `auto` 是「这步是谁替他按的」；null 即真人自己按的
    source: a.auto ?? 'human',
    potBefore: a.potBefore,
    stackBefore: a.stackBefore,
    streetInvestedBefore: a.streetInvestedBefore,
    toCallBefore: a.toCallBefore,
    minRaiseToBefore: a.minRaiseToBefore,
    maxRaiseToBefore: a.maxRaiseToBefore,
    betsBefore: a.betsBefore,
    activeMask: a.activeMask,
    allInMask: a.allInMask,
  }
}

function projectPot(p: { readonly index: number; readonly amount: Chips; readonly eligibleSeats: readonly SeatIndex[] }): Pot {
  // capLevel 不下发（它只在切池算法内部有意义，UI 一处都不读），补 0 占位
  return { index: p.index, amount: p.amount, eligibleSeats: p.eligibleSeats, capLevel: 0 }
}

function projectShowdown(e: ShowdownEntryView, variant: DeckVariant): PublicShowdownEntry {
  if (e.mucked) {
    // ★ 红线 #3：盖牌分支连 category 都没有，这里也绝不补造
    return { seat: e.seat, revealOrder: e.revealOrder, mucked: true, holeCards: null, rank: null }
  }
  const cards = holePair(e.cards)
  const rank = rankFromMadeHand(e.hand, variant)
  if (cards === null || rank === null) {
    // 牌面残缺就退化成「盖牌」而不是画半张牌：UI 宁可少显示，不能显示错的
    return { seat: e.seat, revealOrder: e.revealOrder, mucked: true, holeCards: null, rank: null }
  }
  return { seat: e.seat, revealOrder: e.revealOrder, mucked: false, holeCards: cards, rank }
}

/**
 * 座位 → 手牌内玩家。**只对 `inHand` 的座位调用**：
 * 没参与这手牌的座位在 `PublicHandState.players` 里根本不该出现，
 * 否则 `SeatAt` 会拿到 `status: 'active'` 而把旁观者画成「还在牌局里」。
 */
function projectPlayer(
  s: PublicSeat,
  currentStreet: Street | null,
  actions: readonly PublicAction[],
): PublicHandPlayer {
  const hole = s.holeCards
  // ★ 红线 #1 第二道防线：只有 mine / shown 两个分支带牌
  const cards = hole.visibility === 'mine' || hole.visibility === 'shown' ? holePair(hole.cards) : null
  const hasCards = hole.visibility === 'hidden' || hole.visibility === 'mine' || hole.visibility === 'shown'

  let hasActedThisRound = false
  if (currentStreet !== null) {
    for (const a of actions) {
      if (a.seat === s.index && a.street === currentStreet) {
        hasActedThisRound = true
        break
      }
    }
  }

  return {
    seat: s.index,
    accountId: s.occupant?.accountId ?? '',
    // 协议不下发「本手起始筹码」，但它恒等于「现在的筹码 + 本手已投入」
    //（红线 #19：committedThisHand 不含未跟注退还，而退还已经加回 stack 了）
    startingStack: s.stack + s.committedThisHand,
    stack: s.stack,
    holeCards: cards,
    hasCards,
    status: s.handState === 'live' ? 'active' : s.handState,
    committedThisRound: s.committedThisRound,
    committedThisHand: s.committedThisHand,
    hasActedThisRound,
    lastAction: s.lastAction?.kind ?? null,
    // 'shown' = 已对全场亮出；'mine' 只是自己看得见，不算亮牌
    hasShownCards: hole.visibility === 'shown',
  }
}

function projectResult(view: PlayerView, actions: readonly AppliedAction[]): PublicHandResult | null {
  const hand = view.hand
  const r = hand?.result
  if (hand == null || r == null) return null

  const startingStacks: Record<SeatIndex, Chips> = {}
  const contributedBySeat: Record<SeatIndex, Chips> = {}
  const wonBySeat: Record<SeatIndex, Chips> = {}
  const netBySeat: Record<SeatIndex, number> = {}
  const finalStacks: Record<SeatIndex, Chips> = {}
  for (const s of r.seats) {
    startingStacks[s.seat] = s.startingStack
    contributedBySeat[s.seat] = s.contributed
    wonBySeat[s.seat] = s.won
    netBySeat[s.seat] = s.net
    finalStacks[s.seat] = s.endingStack
  }

  const awards: PotAward[] = r.awards.map((a) => ({
    potIndex: a.potIndex,
    seat: a.seat,
    amount: a.amount,
    // 未摊牌获胜 / 被 muck 的 entry 恒为 null（红线 #3）
    handRank: a.hand === null ? null : rankFromMadeHand(a.hand, variantOfView(view)),
    oddChip: a.oddChip,
  }))

  return {
    handId: r.handId,
    handNo: r.handNo,
    endedBy: r.endedBy,
    buttonSeat: hand.buttonSeat,
    smallBlindSeat: hand.sbSeat,
    bigBlindSeat: hand.bbSeat,
    startingStacks,
    actionLog: actions,
    board: toCardIdsSafe(r.board),
    pots: r.pots.map(projectPot),
    awards,
    uncalledReturn: r.uncalledReturn,
    showdown: r.showdown.map((e) => projectShowdown(e, variantOfView(view))),
    contributedBySeat,
    wonBySeat,
    netBySeat,
    finalStacks,
  }
}

// ============================================================================
// 主函数
// ============================================================================

/**
 * `PlayerView` → 牌桌渲染模型。`view.hand === null`（还没发牌）时返回 `null`，
 * 牌桌会退化成「空桌 + 座位」，这正是等待开局时该看到的样子。
 *
 * @param heroSeat 观察视角。联机模式恒为 `view.viewer.ownedSeat`，
 *                 传进来而不是自己读，是为了让回放/旁观也能复用同一个函数（§A4）。
 */
export function buildTableVM(view: PlayerView | null, heroSeat: SeatIndex | null): PublicHandState | null {
  if (view === null) return null
  const hand = view.hand
  if (hand === null) return null

  const actions = hand.actionLog.map(projectAction)
  const players: Record<SeatIndex, PublicHandPlayer> = {}
  const dealtSeats: SeatIndex[] = []
  for (const s of view.seats) {
    if (!s.inHand) continue
    dealtSeats.push(s.index)
    players[s.index] = projectPlayer(s, hand.street, hand.actionLog)
  }
  dealtSeats.sort((a, b) => a - b)

  return {
    schemaVersion: HAND_STATE_SCHEMA_VERSION,
    handId: hand.handId,
    handNo: hand.handNo,
    config: projectConfig(view, hand.handId),
    viewerSeat: heroSeat,

    buttonSeat: hand.buttonSeat,
    smallBlindSeat: hand.sbSeat,
    bigBlindSeat: hand.bbSeat,
    isHeadsUp: dealtSeats.length === 2,
    deadButton: hand.deadButton,
    dealtSeats,
    players,

    board: toCardIdsSafe(hand.board),

    phase: hand.phase,
    street: hand.street,
    betToMatch: hand.betToMatch,
    lastFullRaiseSize: hand.lastFullRaiseSize,
    lastAggressorSeatThisStreet: hand.lastAggressorSeatThisStreet,
    // ★ 红线 #50 的上游：`actionOn` 一变成 hero，useTableFx 才会 fastForward
    actionOn: hand.actorSeat,

    potTotal: hand.potTotal,
    pots: hand.pots.map(projectPot),
    actionLog: actions,
    uncalledReturn: hand.uncalledReturn,
    showdown: hand.showdown.map((e) => projectShowdown(e, variantOfView(view))),
    result: projectResult(view, actions),
  }
}
